import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingTitle } from '@/components/ui/page-heading';
import {
  FINANCIAL_ORGANISATION_TYPES,
  ORGANISATION_TYPES,
  ORGANISATION_TYPES_REGULATED_FINANCIAL_SERVICES,
  type OrganisationTypeRegulatedFinancialServices,
} from '@/constants/institution';
import type { InstitutionClientCategorisationOrDefaults, InstitutionFuturesClientCategorisation } from '@/features/institution/futures/client-categorisation/schema';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';

const InstitutionFuturesClientCategorisationFieldsetGeneralLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.clientCategorisation.title')}</PageHeadingTitle>
      </legend>
    </PageHeading>
  );
};
InstitutionFuturesClientCategorisationFieldsetGeneralLegend.displayName = 'InstitutionFuturesClientCategorisationFieldsetGeneralLegend';

const InstitutionFuturesClientCategorisationFieldsetGeneral = () => {
  const form = useFormContext<InstitutionClientCategorisationOrDefaults>();
  const { t } = useTranslation();
  const organisationType = form.watch('organisationType');

  return (
    <div className="space-y-6">
      <FormField
        name="organisationType"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.clientCategorisation.organisationType.title')}*</FormLabel>
            <Select
              defaultValue={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                if (!ORGANISATION_TYPES_REGULATED_FINANCIAL_SERVICES.includes(value as OrganisationTypeRegulatedFinancialServices))
                  form.setValue('financialOrganisationType', undefined);
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('institutionDetails.clientCategorisation.placeholders.organisationType')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {ORGANISATION_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`institutionDetails.clientCategorisation.organisationType.options.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {ORGANISATION_TYPES_REGULATED_FINANCIAL_SERVICES.includes(organisationType as OrganisationTypeRegulatedFinancialServices) ? (
        <FormField
          name="financialOrganisationType"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.clientCategorisation.financialOrganisationType.title')}*</FormLabel>
              <Select defaultValue={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('institutionDetails.clientCategorisation.placeholders.financialOrganisationType')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {FINANCIAL_ORGANISATION_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`institutionDetails.clientCategorisation.financialOrganisationType.options.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
InstitutionFuturesClientCategorisationFieldsetGeneral.displayName = 'InstitutionFuturesClientCategorisationFieldsetGeneral';

const beforeStepLeave: FormStepGuard<InstitutionFuturesClientCategorisation> = async ({ direction, form, onSubmit }, next) => {
  if (!form || (direction === 'next' && !(await form.trigger()))) return next(false);

  if (ORGANISATION_TYPES_REGULATED_FINANCIAL_SERVICES.includes(form.getValues('organisationType') as OrganisationTypeRegulatedFinancialServices)) {
    await onSubmit(form.getValues());

    return next(false);
  }

  return next();
};

InstitutionFuturesClientCategorisationFieldsetGeneral.beforeStepLeave = beforeStepLeave;

export { InstitutionFuturesClientCategorisationFieldsetGeneral, InstitutionFuturesClientCategorisationFieldsetGeneralLegend };
